import Joi from 'joi';
import { CampaignStrategy } from '../types/campaign-strategy';
import { getStringEnumValues } from 'src/utils';
import { minFontSize } from '../constants/min-font-size';
import { maxFontSize } from '../constants/max-font-size';
import { minLineHeight } from '../constants/min-line-height';
import { maxLineHeight } from '../constants/max-line-height';
import { defaultFontFamily } from '../constants/default-font-family';
import { minWidth } from '../constants/min-width';
import { maxWidth } from '../constants/max-width';

const fontWeightSchema = Joi.string().valid('normal', 'bold');
const fontStyleSchema = Joi.string().valid('normal', 'italic');
const textAlignSchema = Joi.string().valid('center', 'left', 'right');
const textDecorationSchema = Joi.string().valid('none', 'underline', 'line-through');
const borderStyleSchema = Joi.string().valid('none', 'solid');

const textStyleSchema = Joi.object({
  text: Joi.string().allow(''),
  fontFamily: Joi.string().required().default(defaultFontFamily),
  fontWeight: fontWeightSchema.required().default('normal'),
  fontStyle: fontStyleSchema.required().default('normal'),
  fontSize: Joi.number()
    .required()
    .min(minFontSize)
    .max(maxFontSize)
    .messages({
      'number.min': `Font size should be at least ${minFontSize}px`,
      'number.max': `Font size should be less then ${maxFontSize}px`,
    }),
  lineHeight: Joi.number()
    .required()
    .min(minLineHeight)
    .max(maxLineHeight)
    .messages({
      'number.min': `Line height should be at least ${minLineHeight}%`,
      'number.max': `Line height should be less then ${maxLineHeight}%`,
    }),
  letterSpacing: Joi.number().required().default(0),
  textColor: Joi.string().required(),
  textAlign: textAlignSchema.required(),
  textDecoration: textDecorationSchema.required().default('none'),
});

const headerSchema = textStyleSchema;

const productCardSchema = textStyleSchema.keys({
  field: Joi.string().required(),
});

const CTAButtonSchema = textStyleSchema.keys({
  backgroundColor: Joi.string().required(),
  borderRadius: Joi.number().required(),
  borderWidth: Joi.number().required(),
  borderStyle: borderStyleSchema.required(),
  borderColor: Joi.string().required(),
});

const widgetMakerSchema = Joi.object({
  font: Joi.string().required().default('sans-serif'),
  fontSize: Joi.string().required().default('12px'),
  width: Joi.number()
    .required()
    .default(500)
    .min(minWidth)
    .max(maxWidth)
    .messages({
      'number.min': `Width should be at least ${minWidth}px`,
      'number.max': `Width should be less then ${maxWidth}px`,
    }),
  numberOfRows: Joi.number().required().default(2),
  itemsPerRow: Joi.number().required().default(4),
  textColor: Joi.string().required().default('black'),
  textBackgroundColor: Joi.string().required().default('white'),
  fields: Joi.array().items(Joi.object()).required(),
  title: Joi.string().optional(),
  CTAButtonText: Joi.string().optional(),
  variables: Joi.object().optional(),

  UTMParameters: Joi.object()
    .pattern(Joi.string().min(1).required(), Joi.string().min(1).required())
    .custom(value => {
      if (Object.keys(value).length === 0 || Object.values(value).length === 0) {
        throw new Error('Tracking parameters cannot be empty.');
      }

      Object.keys(value).forEach(key => {
        if (!key.trim()) {
          throw new Error('Invalid tracking parameter key');
        }
      });

      Object.values(value).forEach((val: string) => {
        if (!val.trim()) {
          throw new Error('Invalid tracking parameter value');
        }
      });

      return value;
    }, 'Validate UTM Parameters')
    .messages({
      'any.invalid': 'Invalid tracking parameters',
      'any.custom': 'Tracking parameter is invalid',
      'string.empty': 'Tracking parameter can not be empty',
    }),
  header: headerSchema.optional(),
  productCard: Joi.array().items(productCardSchema).optional(),
  CTAButton: CTAButtonSchema.optional(),
});

export const campaignValidationSchema = {
  campaignName: Joi.string().required().trim().min(1).max(50).messages({
    'string.empty': 'Campaign name can not be empty',
    'string.min': 'Name should be at least 1 character long',
    'string.max': 'Name should be less then 50 characters long',
  }),

  strategy: Joi.string()
    .required()
    .valid(...getStringEnumValues(CampaignStrategy))
    .messages({
      'any.required': 'Please select a recommendation type for this campaign',
    }),

  collectionId: Joi.string()
    .optional()
    .when('strategy', {
      is: CampaignStrategy.Collections,
      then: Joi.required().messages({
        'any.required': 'Please select a collection for this campaign',
      }),
      otherwise: Joi.optional(),
    }),

  widgetMaker: widgetMakerSchema,
};
